import { supabase } from '@/integrations/supabase/client';

export interface ErrorEvent {
  id?: string;
  error_type: 'javascript' | 'network' | 'database' | 'user_action';
  error_message: string;
  error_stack?: string;
  user_id?: string;
  session_id: string;
  page_url: string;
  user_agent: string;
  timestamp: string;
  metadata?: Record<string, any>;
  severity: 'low' | 'medium' | 'high' | 'critical';
  resolved: boolean;
}

export interface PerformanceMetric {
  id?: string;
  metric_type: 'page_load' | 'api_call' | 'component_render' | 'user_action';
  metric_name: string;
  value: number;
  unit: 'ms' | 'seconds' | 'bytes' | 'count';
  user_id?: string;
  session_id: string;
  page_url: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

export class ErrorTrackingService {
  private static sessionId = ErrorTrackingService.generateSessionId();

  static generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Track JavaScript errors
  static async trackError(
    error: Error | string,
    errorType: ErrorEvent['error_type'] = 'javascript',
    severity: ErrorEvent['severity'] = 'medium',
    metadata?: Record<string, any>
  ) {
    try {
      const errorMessage = typeof error === 'string' ? error : error.message;
      const errorStack = typeof error === 'string' ? undefined : error.stack;

      const errorEvent: Omit<ErrorEvent, 'id'> = {
        error_type: errorType,
        error_message: errorMessage,
        error_stack: errorStack,
        session_id: this.sessionId,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        metadata: {
          ...metadata,
          browser: this.getBrowserInfo(),
          screen: this.getScreenInfo(),
        },
        severity,
        resolved: false
      };

      // Store in database
      const { error: dbError } = await (supabase as any)
        .from('error_logs')
        .insert([errorEvent]);

      if (dbError) {
        console.error('Failed to store error in database:', dbError);
      }

      // Also log to console for development
      if (process.env.NODE_ENV === 'development') {
        console.error('Error tracked:', errorEvent);
      }
    } catch (trackingError) {
      console.error('Error tracking failed:', trackingError);
    }
  }

  // Track network errors
  static async trackNetworkError(
    url: string,
    status: number,
    statusText: string,
    metadata?: Record<string, any>
  ) {
    await this.trackError(
      `Network error: ${status} ${statusText} for ${url}`,
      'network',
      status >= 500 ? 'high' : 'medium',
      {
        ...metadata,
        url,
        status,
        statusText
      }
    );
  }

  // Track database errors
  static async trackDatabaseError(
    operation: string,
    error: any,
    metadata?: Record<string, any>
  ) {
    await this.trackError(
      `Database error in ${operation}: ${error.message}`,
      'database',
      'high',
      {
        ...metadata,
        operation,
        dbError: error
      }
    );
  }

  // Track user action errors
  static async trackUserActionError(
    action: string,
    error: any,
    metadata?: Record<string, any>
  ) {
    await this.trackError(
      `User action error in ${action}: ${error.message}`,
      'user_action',
      'medium',
      {
        ...metadata,
        action,
        userError: error
      }
    );
  }

  // Track performance metrics
  static async trackPerformance(
    metricType: PerformanceMetric['metric_type'],
    metricName: string,
    value: number,
    unit: PerformanceMetric['unit'] = 'ms',
    metadata?: Record<string, any>
  ) {
    try {
      const performanceMetric: Omit<PerformanceMetric, 'id'> = {
        metric_type: metricType,
        metric_name: metricName,
        value,
        unit,
        session_id: this.sessionId,
        page_url: window.location.href,
        timestamp: new Date().toISOString(),
        metadata: {
          ...metadata,
          browser: this.getBrowserInfo(),
        }
      };

      const { error } = await (supabase as any)
        .from('performance_metrics')
        .insert([performanceMetric]);

      if (error) {
        console.error('Failed to store performance metric:', error);
      }
    } catch (trackingError) {
      console.error('Performance tracking failed:', trackingError);
    }
  }

  // Track page load time
  static trackPageLoad() {
    if (typeof window !== 'undefined' && window.performance) {
      window.addEventListener('load', () => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (navigation) {
          this.trackPerformance(
            'page_load',
            'total_load_time',
            navigation.loadEventEnd - navigation.fetchStart,
            'ms',
            {
              domContentLoaded: navigation.domContentLoadedEventEnd - navigation.fetchStart,
              firstPaint: navigation.responseEnd - navigation.fetchStart,
            }
          );
        }
      });
    }
  }

  // Track API call performance
  static async trackApiCall(
    apiEndpoint: string,
    duration: number,
    success: boolean,
    metadata?: Record<string, any>
  ) {
    await this.trackPerformance(
      'api_call',
      `api_${success ? 'success' : 'failure'}_${apiEndpoint}`,
      duration,
      'ms',
      {
        ...metadata,
        endpoint: apiEndpoint,
        success
      }
    );
  }

  // Track component render performance
  static async trackComponentRender(
    componentName: string,
    renderTime: number,
    metadata?: Record<string, any>
  ) {
    await this.trackPerformance(
      'component_render',
      `component_${componentName}`,
      renderTime,
      'ms',
      {
        ...metadata,
        component: componentName
      }
    );
  }

  // Track user actions
  static async trackUserAction(
    actionName: string,
    duration: number,
    metadata?: Record<string, any>
  ) {
    await this.trackPerformance(
      'user_action',
      actionName,
      duration,
      'ms',
      metadata
    );
  }

  // Get performance metrics
  static async getPerformanceMetrics(limit = 100) {
    try {
      const { data, error } = await (supabase as any)
        .from('performance_metrics')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to fetch performance metrics:', error);
      return [];
    }
  }

  // Get recent errors
  static async getRecentErrors(limit = 50): Promise<ErrorEvent[]> {
    try {
      const { data, error } = await (supabase as any)
        .from('error_logs')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return (data || []) as ErrorEvent[];
    } catch (error) {
      console.error('Failed to fetch recent errors:', error);
      return [];
    }
  }

  // Get error statistics
  static async getErrorStats(timeRange = 24) {
    try {
      const startTime = new Date();
      startTime.setHours(startTime.getHours() - timeRange);

      const { data, error } = await (supabase as any)
        .from('error_logs')
        .select('error_type, severity')
        .gte('timestamp', startTime.toISOString());

      if (error) throw error;

      // Group by type and severity
      const stats = data?.reduce((acc, error) => {
        const key = `${error.error_type}_${error.severity}`;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return stats || {};
    } catch (error) {
      console.error('Failed to fetch error stats:', error);
      return {};
    }
  }

  // Helper methods
  private static getBrowserInfo() {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    
    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';

    return {
      name: browser,
      userAgent: ua,
      language: navigator.language,
      platform: navigator.platform,
    };
  }

  private static getScreenInfo() {
    return {
      width: window.screen.width,
      height: window.screen.height,
      availWidth: window.screen.availWidth,
      availHeight: window.screen.availHeight,
      colorDepth: window.screen.colorDepth,
    };
  }

  // Initialize error tracking
  static initialize() {
    // Track unhandled JavaScript errors
    window.addEventListener('error', (event) => {
      this.trackError(
        event.error || event.message,
        'javascript',
        'high',
        {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        }
      );
    });

    // Track unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError(
        event.reason,
        'javascript',
        'high',
        {
          type: 'unhandled_promise_rejection',
        }
      );
    });

    // Track page load performance
    this.trackPageLoad();

    console.log('Error tracking initialized');
  }
}